import {
  Key, Activity, ShieldAlert, AlertTriangle, TrendingUp,
  Lock, RefreshCw
} from "lucide-react";
import { CloudEntityQuickLinks } from "./CloudEntityQuickLinks";
import { EntitySignalBar } from "./EntitySignalBar";

interface ExpandedTotalSecretsCardProps {
  data: {
    cloudProvider: string;
    activePct: number;
    alertTrend: number[];
    summary: Record<string, any>;
    rankedSecrets: any[];
    recentAlerts: any[];
  }
}

export const ExpandedTotalSecretsCard = ({ data }: ExpandedTotalSecretsCardProps) => {
  const {
    cloudProvider,
    activePct = 0, alertTrend = [], summary = {},
    rankedSecrets = [], recentAlerts = []
  } = data;

  const trendMax = Math.max(...(alertTrend.length ? alertTrend : [1]), 1);
  const trendPath = alertTrend.length > 0
    ? `M0,100 L${alertTrend.map((v, i) => `${(i / (alertTrend.length - 1)) * 100},${100 - (v / trendMax) * 80}`).join(" L")} L100,100 Z`
    : "";

  return (
    <div className="flex flex-col h-full space-y-6">
      {/* Header and Hero Stats */}
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 rounded-xl bg-sky-500/10">
          <Key className="w-5 h-5 text-sky-500" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-[15px] text-foreground">Total Secrets Deep Dive</h3>
          <p className="text-[13px] text-muted-foreground">Comprehensive visibility into cloud secrets, vaulting status, and associated anomaly alerts.</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div className="p-3.5 rounded-xl bg-card border border-border/40 hover:bg-muted/5 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-sky-500" />
            <span className="text-[12px] font-medium text-muted-foreground">Active Usage</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{activePct}%</div>
        </div>

        <div className="p-3.5 rounded-xl bg-primary/10 border border-primary/20 hover:bg-primary/5 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="w-4 h-4 text-primary" />
            <span className="text-[12px] font-medium text-primary">Alerts Linked</span>
          </div>
          <div className="text-2xl font-bold text-primary">{summary.totalAlerts || 0}</div>
        </div>

        <div className="p-3.5 rounded-xl bg-card border border-border/40 hover:bg-muted/5 transition-colors">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-primary" />
            <span className="text-[12px] font-medium text-muted-foreground">Outside Vaults</span>
          </div>
          <div className="text-2xl font-bold text-foreground">{summary.outsideVaultSecrets || 0}</div>
        </div>

        <div className="p-3.5 rounded-xl bg-card border border-border/40 overflow-hidden relative group">
          <div className="relative z-10 flex items-center justify-between mb-2">
             <div className="flex items-center gap-2">
               <TrendingUp className="w-4 h-4 text-sky-500" />
               <span className="text-[12px] font-medium text-muted-foreground">Alert Trend</span>
             </div>
          </div>
          <div className="absolute inset-0 z-0 top-6 opacity-60 pointer-events-none">
            {alertTrend.length > 0 ? (
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full overflow-visible">
                <defs>
                  <linearGradient id="expandedSecretAlertGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d={trendPath} fill="url(#expandedSecretAlertGrad)" />
                <polyline
                  points={alertTrend.map((v, i) => `${(i / (alertTrend.length - 1)) * 100},${100 - (v / trendMax) * 80}`).join(" ")}
                  fill="none"
                  stroke="hsl(var(--primary))"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : <span className="absolute left-3 top-3 text-[11px] text-muted-foreground">No recent alerts</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6 flex-1 min-h-0">
        {/* Left Column: Ranked Risky Secrets */}
        <div className="col-span-2 flex flex-col gap-3 min-h-0">
          <h4 className="text-[13px] font-bold text-foreground">Secrets Ranked by Alert Risk</h4>
          <div className="rounded-xl border border-border/50 bg-card overflow-hidden flex-1 overflow-y-auto custom-scrollbar">
             {rankedSecrets.length > 0 ? (
               <div className="divide-y divide-border/20">
                 {rankedSecrets.map((secret, idx) => (
                   <div key={idx} className="p-3 hover:bg-muted/10 transition-colors">
                     <div className="flex gap-3">
                       <div className="w-8 h-8 rounded-lg bg-sky-500/10 flex items-center justify-center shrink-0 mt-0.5">
                         <Key className="w-4 h-4 text-sky-500" />
                       </div>
                       <div className="flex-1 min-w-0">
                         <div className="mb-2 min-w-0">
                           <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1.5">
                             <h4 className="min-w-0 flex-1 font-semibold text-[13px] text-foreground truncate" title={secret.name || secret.secretName}>{secret.name || secret.secretName}</h4>
                             <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                               <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                                 {secret.vaulted ? <Lock className="w-3 h-3 text-emerald-500" /> : <AlertTriangle className="w-3 h-3 text-primary" />}
                                 <span>
                                   Location{" "}
                                   <span className={`font-semibold ${secret.vaulted ? "text-emerald-600" : "text-primary"}`}>
                                     {secret.vaulted ? "Vaulted" : "Exposed"}
                                   </span>
                                 </span>
                               </span>
                               <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                                 <RefreshCw className={`w-3 h-3 ${secret.rotationEnabled ? "text-emerald-500" : "text-muted-foreground"}`} />
                                 <span>
                                   Rotation{" "}
                                   <span className="font-semibold text-foreground">
                                     {secret.rotationEnabled ? "Enabled" : "Manual"}
                                   </span>
                                 </span>
                               </span>
                             </div>
                           </div>
                           <p className="text-[11px] text-muted-foreground mt-0.5">{secret.reason || 'No specific reasons identified'}</p>
                           <CloudEntityQuickLinks
                             cloudProvider={cloudProvider}
                             entityKind="secret"
                             entity={secret}
                             className="mt-2"
                           />
                         </div>
                         
                         {/* Details Grid */}
                         <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-border/10">
                            <EntitySignalBar
                              icon={ShieldAlert}
                              label="Alerts"
                              helperText={String(secret.alertCount || 0)}
                              percentage={secret.alertPct}
                              accentClassName="text-primary/80"
                              trackClassName="bg-primary/10"
                              fillClassName="bg-primary"
                            />
                            <EntitySignalBar
                              icon={Activity}
                              label="Activity"
                              percentage={secret.activityPct}
                              accentClassName="text-sky-500/80"
                              trackClassName="bg-sky-500/10"
                              fillClassName="bg-sky-500"
                            />
                         </div>
                       </div>
                     </div>
                   </div>
                 ))}
               </div>
             ) : (
               <div className="p-8 text-center text-muted-foreground text-[13px]">No secrets tracked properly.</div>
             )}
          </div>
        </div>

        {/* Right Column: Recent Anomaly Alerts */}
        <div className="flex flex-col gap-3 min-h-0">
          <h4 className="text-[13px] font-bold text-foreground">Secrets Anomaly Logs</h4>
          <div className="rounded-xl border border-border/50 bg-card overflow-hidden flex-1 overflow-y-auto custom-scrollbar">
            {recentAlerts.length > 0 ? (
              <div className="divide-y divide-border/10">
                {recentAlerts.map((alert, idx) => {
                  const isPrimary = alert.severity?.toLowerCase() === 'critical' || alert.severity?.toLowerCase() === 'high';
                  return (
                    <div key={idx} className="p-3 text-[12px]">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isPrimary ? 'bg-primary/10 text-primary border border-primary/20' : 'bg-sky-500/10 text-sky-500 border border-sky-500/20'}`}>
                          {alert.severity || "INFO"}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {alert.createdAt ? new Date(alert.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'}) : ''}
                        </span>
                      </div>
                      <p className="font-semibold text-foreground leading-tight mb-1">{alert.title}</p>
                      <p className="text-[11px] text-muted-foreground truncate" title={alert.entity}>{alert.entity}</p>
                    </div>
                  );
                })}
              </div>
            ) : (
               <div className="p-6 text-center flex flex-col items-center gap-2">
                 <ShieldAlert className="w-6 h-6 text-muted-foreground/30" />
                 <span className="text-muted-foreground text-[12px]">No recent alerts</span>
               </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
